import React, { useEffect, useState } from "react";
import {
  search_value,
  Filter,
  BellPlus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  BellRing,
  Search,
  RotateCwIcon,
  PackagePlus,
  PackageCheck,
} from "lucide-react";
import Pagination from "../../components/Pagination";
import { usePetPackages } from "../../hooks/usePetPackage";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment/moment";
import PetPackageForm from "./PetPackageForm";
import { toast } from "react-toastify";
import { useResolvedPath } from "react-router-dom";
import Skeleton from "../../components/ui/Skeleton";
import DeleteModal from "../../components/ui/DeleteModal";
import ActionButton from "./ActionButton";

function PetPackageManagement() {
  const [petPackageList, setPetPackageList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPetPackage, setSelectedPetPackage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("view");
  const [statusFilter, setStatusFilter] = useState({});
  const [permission, setPermission] = useState({});
  const queryClient = useQueryClient();
  const path = useResolvedPath();
  const [deleteModal, setDeleteModal] = useState(false);
  const [pageData, setPageData] = useState({
    currentPage: 1,
    totalPages: Math.ceil(petPackageList.length / 10),
    showing: 10,
    total: 0,
  });

  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  const {
    fetchPetPackage,
    createPetPackage,
    deletePetPackage,
    updatePetPackage,
  } = usePetPackages({
    filters: { ...statusFilter, search_value: searchQuery },
    pagination: { page: pageData.currentPage, limit: 10 },
    sort: { field: sortField, direction: sortDirection },
  });

  const petPackageQuery = useQuery({
    queryKey: [
      "petPackage",
      pageData.currentPage,
      statusFilter,
      searchQuery,
      sortField,
      sortDirection,
    ],
    queryFn: fetchPetPackage,
    keepPreviousData: true,
  });

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc" ? "desc" : "asc"
      );
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPageData((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  };

  const openModal = (petPackage, mode) => {
    setSelectedPetPackage(petPackage);
    setModalMode(mode);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPetPackage(null);
  };

  const handlePageChange = (page) => {
    setPageData((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };

  useEffect(() => {
    if (petPackageQuery.data && !petPackageQuery.isLoading) {
      const response = petPackageQuery.data;
      setPetPackageList(response?.data?.data || []);
      setPageData(() => ({
        currentPage: response?.data?.pagination?.currentPage || 1,
        totalPages: response?.data?.pagination?.lastPage || 1,
        total: response?.data?.pagination?.total || 0,
        showing: response?.data?.pagination?.showing || 10,
      }));
    }
  }, [
    petPackageQuery.data,
    pageData.showing,
    pageData.currentPage,
    petPackageQuery.isLoading,
  ]);

  const handleDelete = {
    onDelete: (petPackage) =>
      mutation.deletePetPackage.mutate(petPackage._id, {
        onSuccess: async (data) => {
          toast.success(data.message || "PetPackage Deleted");
          setDeleteModal(false);
        },
      }),
    onClose: () => setDeleteModal(false),
    onOpen: (petPackage) => {
      setDeleteModal(true);
      setSelectedPetPackage(petPackage);
    },
  };

  const mutation = {
    createPetPackage: useMutation({
      mutationFn:
        modalMode === "edit"
          ? async (body) =>
              await updatePetPackage({ id: body._id, userData: body })
          : async (body) => await createPetPackage(body),
    }),
    deletePetPackage: useMutation({
      mutationFn: async (id) => await deletePetPackage(id),
    }),
  };

  const handleSubmit = (body) => {
    mutation.createPetPackage.mutate(body, {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries(["petPackage"]);
        toast.success(
          data?.data?.message ||
            `Pet Package ${
              modalMode === "edit" ? "Updated" : "Created"
            } successfully`
        );
        setShowModal(false);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });
  };

  return (
    <div>
      <main>
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold">Add On Management</h1>
          {permission.add && (
            <button
              onClick={() => openModal({}, "create")}
              className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
            >
              <PackagePlus className="mr-2 h-4 w-4" />
              Add New Add On
            </button>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search Add On..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 border rounded-md w-10/12 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {petPackageQuery.isLoading ? (
            <Skeleton.Card header rows={10} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th
                      className="px-4 py-3 text-sm font-medium text-gray-500 border-b cursor-pointer select-none"
                      onClick={() => handleSort("title")}
                    >
                      Title
                      {sortField === "title" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                    <th
                      className="px-4 py-3 text-sm font-medium text-gray-500 border-b cursor-pointer select-none"
                      onClick={() => handleSort("description")}
                    >
                      Description
                      {sortField === "header" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                    <th
                      className="px-4 py-3 text-sm font-medium text-gray-500 border-b cursor-pointer select-none"
                      onClick={() => handleSort("created_at")}
                    >
                      Creation Date
                      {sortField === "created_at" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {petPackageList.length > 0 ? (
                    petPackageList.map((petPackage) => (
                      <tr
                        key={petPackage._id}
                        className="hover:bg-gray-50 border-b"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                              <PackageCheck className="h-4 w-4 text-gray-600" />
                            </div>
                            <div className="ml-3">
                              <div className="font-medium text-gray-900 capitalize md:uppercase">
                                {petPackage.title}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {petPackage.description || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {moment(petPackage.created_at).format("DD-MM-yyyy")}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <ActionButton
                            handleDelete={handleDelete}
                            item={petPackage}
                            openModal={openModal}
                            setPermission={setPermission}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No petPackage found matching your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          <Pagination
            currentPage={pageData.currentPage}
            onPageChange={handlePageChange}
            showing={pageData.showing}
            totalPages={pageData.totalPages}
            total={pageData.total}
          />
        </div>
      </main>

      <DeleteModal
        isOpen={deleteModal}
        onClose={handleDelete.onClose}
        onDelete={() => handleDelete.onDelete(selectedPetPackage)}
        title="PetPackage"
      />

      {/* PetPackage Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={closeModal}
          ></div>
          <div className="h-4/5 relative bg-white rounded-2xl max-w-2xl w-full mx-4 shadow-xl overflow-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium">
                {modalMode === "view"
                  ? "Add On Details"
                  : modalMode === "edit"
                  ? "Edit Add On"
                  : "Create Add On"}
              </h3>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                onClick={closeModal}
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <PetPackageForm
                mode={modalMode}
                defaultValues={
                  {
                    ...selectedPetPackage,
                    created_at: moment(selectedPetPackage.created_at).format(
                      "YYYY-MM-DD"
                    ),
                  } || {}
                }
                onCancel={closeModal}
                onSubmit={(data) => handleSubmit(data)}
                path={path.pathname}
                submissionLoading={mutation.createPetPackage.isPending}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PetPackageManagement;
